import React, { useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Drawer,
  IconButton,
  Tab,
  Tabs,
  Button,
  Chip,
  Card,
  CardHeader,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomRadarChart from "../shared/RadarChart";
import useCustomMediaQuery from "../../hooks/useMediaQuery";
import { Talent } from "src/features/talents/talentsTypes";
import MatchScores from "./MatchScore";
import CustomStepper from "../shared/Stepper";
import { buildRadarData } from "src/features/talents/matchingUtils";
import { useTranslation } from "react-i18next";
import TalentAvatar from "./TalentAvatar";
import {
  getMaskedTalentName,
  getNameInitial,
  useShouldMaskTalentIdentity,
} from "src/utils/talentPrivacy";

interface TalentDetailDrawerProps {
  open: boolean;
  talent: Talent;
  positionSkillNames: string[];
  onClose: () => void;
  onRequestInterview: (talent: Talent) => void;
  requestInterviewLoading: boolean;
  onToggleHidden: (talent: Talent, nextHidden: boolean) => void;
  hideLoading: boolean;
}

const drawerWidth = "calc(100% - 400px)";
const normalizeSkillName = (value?: string) => (value || "").trim().toLowerCase();
type DisplayedSkill = {
  name: string;
  point: number;
  isRelevant: boolean;
};

const getDisplayedSkillPriority = (skill: DisplayedSkill) => {
  if (!skill.isRelevant) {
    return 3;
  }

  if (skill.point >= 80) {
    return 0;
  }

  if (skill.point >= 60) {
    return 1;
  }

  return 2;
};

const sortDisplayedSkills = (skills: DisplayedSkill[]) =>
  [...skills].sort((left, right) => {
    const priorityDiff =
      getDisplayedSkillPriority(left) - getDisplayedSkillPriority(right);
    if (priorityDiff !== 0) {
      return priorityDiff;
    }

    const pointDiff = right.point - left.point;
    if (pointDiff !== 0) {
      return pointDiff;
    }

    return left.name.localeCompare(right.name);
  });

const TalentDetailDrawer = ({
  open,
  onClose,
  talent,
  positionSkillNames,
  onRequestInterview,
  requestInterviewLoading,
  onToggleHidden,
  hideLoading,
}: TalentDetailDrawerProps) => {
  const { isMobileView } = useCustomMediaQuery();
  const { t } = useTranslation();
  const shouldMaskTalentIdentity = useShouldMaskTalentIdentity();

  const radarData = buildRadarData(talent.match?.groupScores || []);
  const overallScore = Math.round(
    talent.match?.overallScore ?? Number(talent.completeness) ?? 0
  );
  const normalizedPositionSkillNames = new Set(
    (positionSkillNames || [])
      .map((skillName) => normalizeSkillName(skillName))
      .filter(Boolean)
  );
  const hasPositionSkills = normalizedPositionSkillNames.size > 0;
  const displayedSkills = sortDisplayedSkills(
    talent.match?.skillFitness && talent.match.skillFitness.length
      ? talent.match.skillFitness.map((skill) => ({
          name: skill.name,
          point: skill.score,
          isRelevant: skill.isRelevant ?? true,
        }))
      : talent.skills.map((skill) => ({
          name: skill.name,
          point: skill.point,
          isRelevant:
            hasPositionSkills &&
            normalizedPositionSkillNames.has(normalizeSkillName(skill.name)),
        }))
  );

  const [value, setValue] = useState("1");

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  const isRequestDisabled =
    !talent.match?.matchId ||
    talent.status === "requested" ||
    talent.status === "accepted" ||
    talent.status === "rejected";
  const isHidden = talent.match?.isHidden ?? false;
  const isHideDisabled = !talent.match?.matchId || hideLoading;
  const displayName = shouldMaskTalentIdentity
    ? getMaskedTalentName(talent.firstName, talent.lastName)
    : `${talent.firstName} ${talent.lastName}`.trim();
  const avatarFallback = shouldMaskTalentIdentity
    ? getMaskedTalentName(talent.firstName, talent.lastName)
    : talent.firstName?.charAt(0);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="right"
      variant="persistent"
      sx={{
        flexShrink: 0,
        width: isMobileView ? "100%" : drawerWidth,
        "& .MuiDrawer-paper": {
          boxSizing: "border-box",
          position: "relative",
        },
      }}
    >
      {talent && (
        <Box>
          <IconButton
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
          <Box sx={sxProps.avatarBox}>
            <Box sx={{ ...sxProps.row, alignItems: "center" }}>
              <TalentAvatar
                profileId={talent.id}
                avatarId={talent.avatarId}
                fallbackText={avatarFallback || getNameInitial(talent.firstName)}
                blurred={shouldMaskTalentIdentity}
                sx={{ width: 100, height: 100, mr: 2 }}
              />
              <Box>
                <Typography variant="h6">
                  {displayName}
                </Typography>
                <Typography variant="body1">{talent.headline}</Typography>
              </Box>
            </Box>

            <Box sx={{ ...sxProps.column, pr: 6 }}>
              <Button
                variant="contained"
                onClick={() => onRequestInterview(talent)}
                disabled={isRequestDisabled || requestInterviewLoading}
                startIcon={
                  requestInterviewLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : undefined
                }
              >
                {requestInterviewLoading
                  ? t("talents.sendingInterviewRequest")
                  : talent.status === "requested"
                  ? t("talents.requestSent")
                  : t("talents.requestInterview")}
              </Button>
              <Button
                variant="outlined"
                onClick={() => onToggleHidden(talent, !isHidden)}
                disabled={isHideDisabled}
              >
                {isHidden ? t("talents.unhideCandidate") : t("talents.hideCandidate")}
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="lab API tabs example"
              variant="scrollable"
              sx={{
                minHeight: "40px",
                ".MuiTabs-scroller": {
                  display: "flex",
                  justifyContent: isMobileView ? "flex-start" : "center",
                },
                button: {
                  padding: "4px",
                  fontSize: "12px",
                  minWidth: isMobileView ? "45px" : "90px",
                },
              }}
            >
              <Tab label="Match Score" value="1" />
              <Tab label="Overview" value="2" />
              <Tab label="Notes" value="3" />
              <Tab label="Feedbacks" value="4" />
              <Tab label="Email" value="5" />
              <Tab label="Forms" value="6" />
            </Tabs>
          </Box>
          <Box sx={sxProps.row}>
            <Box sx={{ ...sxProps.column, width: "100%", p: 4, gap: 4 }}>
              <CustomRadarChart
                data={radarData}
                name={displayName}
                point={String(overallScore)}
              />
              {displayedSkills.length ? (
                <Card sx={{ boxShadow: 4, borderRadius: 0 }}>
                  <CardHeader
                    sx={{ bgcolor: "background.default" }}
                    title="Skills"
                  />
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, p: 2 }}>
                    {displayedSkills.map((skill) => {
                      const isRelevant = skill.isRelevant;
                      const isLowRelevantSkill = isRelevant && skill.point < 60;
                      const isMidRelevantSkill =
                        isRelevant && skill.point >= 60 && skill.point < 80;
                      const isHighRelevantSkill = isRelevant && skill.point >= 80;

                      return (
                        <Chip
                          key={skill.name}
                          variant="outlined"
                          avatar={
                            <Avatar
                              sx={{
                              color: "white !important",
                              bgcolor: !isRelevant
                                  ? "grey.500"
                                  : isLowRelevantSkill
                                    ? "error.dark"
                                    : isHighRelevantSkill
                                      ? "success.main"
                                      : isMidRelevantSkill
                                        ? "warning.main"
                                        : "grey.500",
                              }}
                            >
                              {skill.point}
                            </Avatar>
                          }
                          label={skill.name}
                          color={
                            !isRelevant
                              ? undefined
                              : isLowRelevantSkill
                                ? undefined
                                : isHighRelevantSkill
                                  ? "success"
                                  : isMidRelevantSkill
                                    ? "warning"
                                    : undefined
                          }
                          sx={{
                            fontWeight: "bold",
                            ...(isRelevant
                              ? {
                                  color: isLowRelevantSkill
                                    ? "error.dark"
                                    : isHighRelevantSkill
                                      ? "success.dark"
                                      : isMidRelevantSkill
                                        ? "warning.dark"
                                        : "grey.700",
                                  borderColor: isLowRelevantSkill
                                    ? "error.dark"
                                    : isMidRelevantSkill
                                      ? undefined
                                      : "grey.300",
                                }
                              : {
                                  bgcolor: "grey.100",
                                  borderColor: "grey.300",
                                  color: "grey.700",
                                }),
                          }}
                        />
                      );
                    })}
                  </Box>
                </Card>
              ) : null}
              <CustomStepper />
            </Box>
            <MatchScores
              groupScores={talent.match?.groupScores || []}
            />
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

export default TalentDetailDrawer;

const sxProps = {
  row: {
    display: "flex",
    justifyContent: "space-between",
  },
  avatarBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    px: 3,
    pt: 4,
    button: {
      textTransform: "capitalize",
    },
  },
  column: {
    display: "flex",
    flexDirection: "column",
  },
};
